// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from './firebase';
import { createBackup } from './utils/backup';
import { doc, setDoc, getDoc, collection, addDoc, query, where, getDocs } from 'firebase/firestore';

import { CarTemplates } from './components/VehicleEditor/DefaultParameters';
import { DefaultHelpTips } from './components/VehicleEditor/DefaultHelpTips';


// Public pages
import Landing from './pages/Landing';
import Pricing from './pages/Pricing';
import Login from './pages/Login';
import Register from './pages/Register';
import TermsOfService from './pages/TermsOfService';
import Changelog from './pages/Changelog';
import About from './pages/About';

// Authenticated app pages
import Main from './pages/Main';
import VehicleEditor from './pages/VehicleEditor';
import SessionSetup from './pages/SessionSetup';
import SessionData from './pages/SessionData';
import EventPage from './pages/EventPage';
import Settings from './pages/Settings';
import Account from './pages/Account';
import Help from './pages/Help';
import SetupHistory from './pages/SetupHistory';
import TireManager from './pages/TireManager';
import CircuitManager from './pages/CircuitManager';
import ToolsHub from './tools/ToolsHub';
import ToolRoute from './tools/ToolRoute';
import Excuses from './pages/Excuses';

// Components

// Function to check subscription status
const checkSubscriptionStatus = async (user) => {
  if (!user) return { hasAccess: false, status: 'none', plan: null, loading: false };

  try {
    // Check if user already has lifetime access
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (userDoc.exists() && userDoc.data().lifetime) {
      return { hasAccess: true, status: 'active', plan: 'pro', loading: false };
    }
    // Check Stripe subscriptions from customers collection
    const subscriptionsRef = collection(db, 'customers', user.uid, 'subscriptions');
    const q = query(subscriptionsRef, where('status', 'in', ['active', 'trialing', 'past_due', 'canceled']));
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      // Find active subscriptions first, prioritize Pro if multiple active
      const activeSubscriptions = snapshot.docs.filter(doc => doc.data().status === 'active');

      let selectedSub;
      if (activeSubscriptions.length > 0) {
        // If multiple active subscriptions, prioritize Pro
        const proSubscription = activeSubscriptions.find(doc => {
          const data = doc.data();
          return data.items && Array.isArray(data.items) &&
                 data.items[0]?.product?.name === 'Pro';
        });
        selectedSub = proSubscription || activeSubscriptions[0];
      } else {
        // No active subscriptions, use the first one found
        selectedSub = snapshot.docs[0];
      }

      const sub = selectedSub.data();
      const status = sub.status === 'trialing' ? 'trial' : sub.status === 'active' ? 'active' : sub.status;

      // Determine plan from product name in subscription items
      let plan = 'enthusiast'; // default
      console.log('Subscription items:', sub.items);

      if (sub.items && Array.isArray(sub.items)) {
        const item = sub.items[0];
        console.log('First item keys:', Object.keys(item));
        console.log('Full item:', JSON.stringify(item, null, 2));

        // Try different ways to access product info
        let productName = null;

        // Check the correct location: item.price.product.name
        if (item.price?.product?.name) {
          productName = item.price.product.name;
          console.log('Found product name in item.price.product.name:', productName);
        } else if (item.product?.name) {
          productName = item.product.name;
          console.log('Found product name in item.product.name:', productName);
        } else {
          console.log('Product field type:', typeof item.product);
          console.log('Product field value:', item.product);
          console.log('Price product:', item.price?.product);
        }

        if (productName) {
          if (productName === 'Pro') {
            plan = 'pro';
          } else if (productName === 'Enthusiast') {
            plan = 'enthusiast';
          }
          console.log('Determined plan:', plan);
        } else {
          console.log('Could not find product name, using default plan: enthusiast');
        }
      } else {
        console.log('No items array found');
      }

      // Update user document with subscription info
      const subscriptionId = selectedSub?.id || 'unknown';

      // Handle both Unix timestamp (number) and Firestore Timestamp
      let accessEndDate = null;
      if (sub.current_period_end) {
        if (typeof sub.current_period_end === 'number') {
          accessEndDate = new Date(sub.current_period_end * 1000).toISOString();
        } else if (sub.current_period_end.seconds) {
          // Firestore Timestamp
          accessEndDate = new Date(sub.current_period_end.seconds * 1000).toISOString();
        } else if (sub.current_period_end.toDate) {
          // Firestore Timestamp with toDate method
          accessEndDate = sub.current_period_end.toDate().toISOString();
        }
      }

      const updateData = {
        subscriptionStatus: status,
        plan: plan,
        subscriptionId: subscriptionId,
        accessEndDate: accessEndDate,
        lastUpdated: new Date().toISOString()
      };

      await setDoc(doc(db, 'users', user.uid), updateData, { merge: true });

      const hasAccess = status === 'active' || status === 'trial';
      return { hasAccess, status, plan, loading: false };
    }

    // Check for lifetime payments
    const paymentsRef = collection(db, 'customers', user.uid, 'payments');
    const paymentsQuery = query(paymentsRef, where('status', '==', 'succeeded'));
    const paymentsSnapshot = await getDocs(paymentsQuery);
    if (!paymentsSnapshot.empty) {
      // Has lifetime payment
      await setDoc(doc(db, 'users', user.uid), {
        lifetime: true,
        subscriptionStatus: 'active',
        plan: 'pro',
        accessEndDate: new Date('3000-01-01').toISOString()
      }, { merge: true });
      return { hasAccess: true, status: 'active', plan: 'pro', loading: false };
    }

    // Check trial status
    const trialUserDoc = await getDoc(doc(db, 'users', user.uid));
    if (trialUserDoc.exists()) {
      const data = trialUserDoc.data();
      if (data.lifetime) {
        return { hasAccess: true, status: 'active', plan: 'pro', loading: false };
      }
      if (data.subscriptionStatus === 'trial') {
        const accessEnd = new Date(data.accessEndDate);
        if (accessEnd > new Date()) {
          return { hasAccess: true, status: 'trial', plan: data.plan || 'pro', loading: false };
        } else {
          // Trial expired, update status
          await setDoc(doc(db, 'users', user.uid), { subscriptionStatus: 'expired' }, { merge: true });
          return { hasAccess: false, status: 'expired', plan: null, loading: false };
        }
      }
      // Check for existing subscription status
      if (data.subscriptionStatus) {
        return {
          hasAccess: data.subscriptionStatus === 'active',
          status: data.subscriptionStatus,
          plan: data.plan || 'enthusiast',
          loading: false
        };
      }
    }
    return { hasAccess: false, status: 'none', plan: null, loading: false };
  } catch (error) {
    console.error('Error checking subscription:', error);
    return { hasAccess: false, status: 'none', plan: null, loading: false };
  }
};

const convertToGroups = (params) => {
  if (Array.isArray(params)) {
    return [{ title: 'Default', params }];
  }
  return Object.entries(params).map(([title, params]) => ({ title, params }));
};

const populateDefaultHelp = (tabs) => {
  tabs.forEach(tab => {
    tab.groups.forEach(group => {
      group.params.forEach(param => {
        if (!param.help && DefaultHelpTips[param.name]) {
          param.help = DefaultHelpTips[param.name];
        }
      });
    });
  });
};

const defaultTabs = [
  { name: 'Info', groups: [{ title: 'General', params: [{ name: 'Vehicle Name', type: 'text', value: '' }, { name: 'Image', type: 'upload', value: '' }, { name: 'Vehicle Notes', type: 'textarea', value: '' }] }] },
  { name: 'Suspension', groups: convertToGroups(CarTemplates['Club Track Day'].Metric.Suspension) },
  { name: 'Tires', groups: convertToGroups(CarTemplates['Club Track Day'].Metric.Tires) },
  { name: 'Brakes', groups: convertToGroups(CarTemplates['Club Track Day'].Metric.Brakes) },
  { name: 'Aero', groups: convertToGroups(CarTemplates['Club Track Day'].Metric.Aero) },
  { name: 'Drivetrain', groups: convertToGroups(CarTemplates['Club Track Day'].Metric.Drivetrain) },
  { name: 'Engine', groups: convertToGroups(CarTemplates['Club Track Day'].Metric.Engine) },
  { name: 'Electronics', groups: convertToGroups(CarTemplates['Club Track Day'].Metric.Electronics) },
];

populateDefaultHelp(defaultTabs);

function App() {
  const [user, loading, error] = useAuthState(auth);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState({ loading: true });

  // Check subscription status
  useEffect(() => {
    if (user) {
      setSubscriptionStatus({ loading: true });
      checkSubscriptionStatus(user).then(setSubscriptionStatus);
    } else {
      setSubscriptionStatus({ hasAccess: false, status: 'none', loading: false });
    }
  }, [user]);

  // Auto-backup every 5 minutes when logged in
  useEffect(() => {
    if (user) {
      const interval = setInterval(() => {
        createBackup(user);
      }, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Periodic subscription status check every 6 hours
  useEffect(() => {
    if (user) {
      const interval = setInterval(() => {
        checkSubscriptionStatus(user).then(setSubscriptionStatus);
      }, 6 * 60 * 60 * 1000); // 6 hours
      return () => clearInterval(interval);
    }
  }, [user]);





  // Only block on auth resolution. Once we know the user is logged in, let the
  // page (e.g. Main) render its own skeleton while subscription status loads,
  // so there's no separate white-screen spinner phase.
  if (loading) {
    return (
      <div style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        fontFamily: 'Montserrat, sans-serif'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #3498db',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ marginTop: '20px' }}>Loading...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Not logged in → public marketing site
  if (!user) {
    return (
      <Router>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/changelog" element={<Changelog />} />
          <Route path="/about" element={<About />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    );
  }

  // Logged in → full app (including after Stripe trial)
  return (
    <Router>
      <Routes>
        {/* Main dashboard */}
        <Route
          path="/app"
          element={<Main setSelectedVehicle={setSelectedVehicle} setSelectedEvent={setSelectedEvent} subscriptionStatus={subscriptionStatus} />}
        />
        <Route path="/" element={<Navigate to="/app" replace />} />

        {/* All app routes */}
        <Route path="/vehicle-editor/:id" element={<VehicleEditor subscriptionStatus={subscriptionStatus} />} />
        <Route path="/event/:id" element={<EventPage />} />
        <Route path="/session-setup/:sessionId?" element={<SessionSetup />} />
        <Route path="/session-data/:sessionId" element={<SessionData />} />
        <Route path="/setup-history/:vehicleId" element={<SetupHistory subscriptionStatus={subscriptionStatus} />} />
        <Route path="/tire-manager" element={<TireManager subscriptionStatus={subscriptionStatus} />} />
        <Route path="/circuit-manager" element={<CircuitManager />} />
        <Route path="/tools" element={<ToolsHub />} />
        <Route path="/tools/:toolId" element={<ToolRoute />} />
        {/* Old Calculators URLs */}
        <Route path="/calculators" element={<Navigate to="/tools" replace />} />
        <Route path="/brake-bias-calculator" element={<Navigate to="/tools/brake-bias" replace />} />
        <Route path="/tire-size-calculator" element={<Navigate to="/tools/tire-size" replace />} />
        <Route path="/wheel-rate-calculator" element={<Navigate to="/tools/wheel-rate" replace />} />
        <Route path="/excuses" element={<Excuses />} />
        <Route path="/settings" element={<Settings subscriptionStatus={subscriptionStatus} />} />
        <Route path="/account" element={<Account subscriptionStatus={subscriptionStatus} />} />
        <Route path="/help" element={<Help />} />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
    </Router>
  );
}

export default App;